<template>
    <div class="bubble-msg" :class="classes">
      <div v-if="displayStaffName"
           class="staff-name">
        {{displayStaffName}}
      </div>
      <UserIsTypingBubble v-if="isTyping"/>
      <component v-else
                 @event-fired="$emit('event-fired', $event)"
                 :is="bubbleCmp"
                 :showStaff="showStaff"
                 :message="message"/>
      <footer class="bubble-footer">

        <v-tooltip bottom
                   content-class="indicator-tooltip"
                   v-if="message.message_of !== 'messaging_channel'">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-if="channel"
              class="channel"
              size="14"
              v-bind="attrs"
              v-on="on">
              {{channel.icon}}
            </v-icon>
          </template>
          <span>{{channel && channel.app_name}}</span>
        </v-tooltip>

        <v-tooltip bottom
                   v-if="this.isBusinessMessage && (message.mail_delivered || message.mail_read)"
                   content-class="indicator-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <span v-if="showIndication"
                  class="msg-status"
                  :class="{'mail-read': message.mail_read}"
                  v-bind="attrs"
                  v-on="on">
              <v-icon class="mail-delivered">
                icon-Double-v
              </v-icon>
            </span>
          </template>
          <span>
            {{indicatorTooltip}}
          </span>
        </v-tooltip>

        <v-tooltip bottom
                   v-else-if="this.isBusinessMessage && message.mail_sent"
                   content-class="indicator-tooltip">
          <template v-slot:activator="{ on, attrs }">
            <span class="msg-status"
                  :class="{'mail-read': message.mail_read}"
                  v-bind="attrs"
                  v-on="on">
              <v-icon size="12" class="mail-sent">
                icon-v
              </v-icon>
            </span>
          </template>
          <span>
            {{indicatorTooltip}}
          </span>
        </v-tooltip>

        <span class="created">{{message.created_at_h}}</span>
      </footer>
    </div>
</template>

<script>
import {
  AppointmentBubble,
  AppointmentSeriesBubble,
  ContactBubble,
  ReviewBubble,
  TextBubble,
  AnnouncementBubble,
  InvoiceBubble,
  EstimateBubble,
  PaymentBubble,
  PaymentStatusBubble,
  EventBubble,
  EventSeriesBubble,
  PackageBubble,
  ProductBubble,
  AppSession,
  UserIsTypingBubble
} from './messageables'
import {mapGetters} from "vuex";

export default {
  name: 'BubbleMsg',
  components: {
    UserIsTypingBubble,
    AppointmentBubble,
    AppointmentSeriesBubble,
    ContactBubble,
    ReviewBubble,
    TextBubble,
    AnnouncementBubble,
    InvoiceBubble,
    EstimateBubble,
    PaymentBubble,
    PaymentStatusBubble,
    EventBubble,
    EventSeriesBubble,
    PackageBubble,
    ProductBubble,
    AppSession
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    showStaff: {
      type: Boolean,
      required: true,
      default: false
    },
    showChannel: {
      type: Boolean,
      default: false
    },
    isTyping: {
      type: Boolean,
    }
  },
  data: () => ({bubbleCmp: null}),
  created() {
    switch (this.message.message_of) {
      case 'appointment_series':
        this.bubbleCmp = 'AppointmentSeriesBubble'
        break;
      case 'appointment':
        this.bubbleCmp = 'AppointmentBubble'
        break;
      case 'message':
        const contactBubbles = {
          "api": 'ContactBubble',
          "contact_form": 'ContactBubble',
          "external_contact_message": 'ContactBubble',
        }

        this.bubbleCmp = contactBubbles[this.message.sub_type] || 'TextBubble'
        break;
      case 'messaging_channel':
        this.bubbleCmp = 'AppSession'
        break;
      case 'contact_form':
      case 'document':
        this.bubbleCmp = 'ContactBubble'
        break;
      case 'review':
        this.bubbleCmp = 'ReviewBubble'
        break;
      case 'announcement':
        this.bubbleCmp = 'AnnouncementBubble'
        break;
      case 'invoice':
        this.bubbleCmp = 'InvoiceBubble'
        break;
      case 'estimate':
        this.bubbleCmp = 'EstimateBubble'
        break;
      case 'payment':
        this.bubbleCmp = 'PaymentBubble'
        break;
      case 'payment_status':
        this.bubbleCmp = 'PaymentStatusBubble'
        break;
      case 'event_attendance':
      case 'waitlist':
        this.bubbleCmp = 'EventBubble'
        break;
      case 'event_series_attendance':
        this.bubbleCmp = 'EventSeriesBubble'
        break;
      case 'client_booking_package':
        this.bubbleCmp = 'PackageBubble'
        break;
      case 'product_order':
        this.bubbleCmp = 'ProductBubble'
        break;
    }
  },
  computed: {
    ...mapGetters('accountModule',['channels']),
    classes() {
      const classes = [this.message.direction]

      if (this.displayStaffName) {
        classes.push('has-margin-top')
      }

      return classes
    },
    channel() {
      if (!this.showChannel || this.message.sub_type !== "text" || !this.message.channels) return null
      return this.channels.find(channel => channel.value === this.message.channels[0])
    },
    isBusinessMessage() {
      return this.message.direction === 'business_to_client'
    },
    isClientMessage() {
      return this.message.direction === 'client_to_business'
    },
    displayStaffName() {
      if (!this.showStaff || !this.isBusinessMessage) return null

      const {messageable} = this.message
      if (messageable && messageable.staff) {
          return this.message.messageable.staff.display_name
      }

      if (this.message.staff) {
        return this.message.staff.display_name
      }

      return null
    },
    indicatorTooltip() {
      if (this.message.mail_read) {
        return this.$t('conversation.info.read');
      } else if (this.message.mail_delivered) {
        return this.$t('conversation.info.delivered');
      } else {
        return this.$t('conversation.info.sent');
      }
    },
    showIndication() {
      if (!this.isBusinessMessage) return false
      return this.message.mail_delivered || this.message.mail_read || this.message.mail_sent
    }
  }
}
</script>
<style lang="scss">
@import '@/assets/scss/abstract/_variables';
@import '@/assets/scss/abstract/_i18n-mixins';

.bubble-msg {
  $footerHeight: 24px;

  display: inline-block;
  font-family: var(--primary-font-family);
  min-width: 215px;
  padding: 9px 12px;
  margin-bottom: $footerHeight;
  border-radius: 10px;
  border: 1px solid $grey-color-7;
  font-size: 12px;
  position: relative;

  &.typing {
    min-width: 44px;
  }

  &.has-margin-top {
    margin-top: $footerHeight;
  }

  &.to_business {
    background-color: $white-color-1;
  }

  &.client_to_business {
    background-color: $actionDotsHover;
    border-top-left-radius: 0;
  }

  &.business_to_client {
    background-color: $blue-color-1;
    border-top-right-radius: 0;
    border-color: transparent;
  }

  .staff-name {
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    text-align: end;
    position: absolute;
    left: 0;
    right: 0;
    top: -18px;
    font-size: 11px;
    font-weight: 500;
  }

  .bubble-footer {
    height: $footerHeight;
    padding: 0 5px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    font-size: 11px;
    font-weight: 500;
    position: absolute;
    left: 0;
    right: 0;
    bottom: -$footerHeight;

    .msg-status {
      &.mail-read {
        .theme--light.v-icon {
          color: $toast_announce_bg;
        }
      }

      .mail-sent {
        @include i-margin-mirror(0, 3px);
      }
    }

    .created {
      color: $color_dark_sec_text;
    }

    .channel {
      @include i-margin-mirror(0, 4px);

      .icon-delivered_thin:before {
        transform: translateY(-2px);
      }
    }
  }
}

.indicator-tooltip {
  font-family: var(--primary-font-family);
  font-size: 11px;
  line-height: 13px;
  text-align: center;
  padding: 5px 8px;
}
</style>
